<template>
  <div
    ref="toggleRef"
    class="filter-block"
    :class="{
      'filter-block-responsive': responsive,
      checked: value,
      'filter-block--gradient-border': gradientBorder,
    }"
  >
    <label
      class="toggle"
      :class="{
        isChecked: value && !responsive,
        'toggle-responsive': responsive,
      }"
    >
      <span class="toggle-label body-m-regular">{{ title }}</span>
      <input
        :checked="value"
        class="toggle-checkbox"
        type="checkbox"
        @change="onChange"
      />
      <span class="toggle-switch"></span>
    </label>
  </div>
</template>

<script setup lang="ts">
const emit = defineEmits([
  'changeToggle',
  'position',
  'setFilters',
  'onBooleanChange',
]);
const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  responsive: {
    type: Boolean,
    default: false,
  },
  gradientBorder: {
    type: Boolean,
    default: false,
  },
  emitPosition: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  id: {
    type: Object as any,
    default: () => {},
  },
});

const toggleRef = ref<HTMLElement | null>(null);

const onChange = (event: Event): void => {
  emit('changeToggle', !!(event.target as HTMLInputElement)?.checked);

  if (props.emitPosition && toggleRef.value) {
    const { right, top, height } = toggleRef.value.getBoundingClientRect();
    const pos = { right, top: top + height / 2 + window.scrollY };
    emit('position', pos);
  }
  if (props.id) {
    emit('onBooleanChange', {
      id: props.id,
      val: props.value ? undefined : true,
      queryVal: props.value ? undefined : true,
      changeIn: 'filters',
    });
  }
  emit('setFilters');
};
</script>
<style scoped lang="scss">
.filter-block {
  display: inline-block;
  max-width: 218px;
  width: max-content;

  &--gradient-border {
    border: 1px solid transparent;
    display: flex;
    align-items: center;

    &.filter-block-responsive {
      border-width: 2px;

      &.checked {
        border-width: 2px;
      }

      height: 44px;
    }

    &.checked {
      border: 1px solid #3616d0;
    }

    border-radius: 8px;
    background: linear-gradient(#f5f5f5, #f5f5f5) padding-box,
      linear-gradient(90deg, #fb2386, #3616d0, #242424, #9752ff) border-box;

    .toggle {
      border: none;
      height: 28px;
      @include lg-block {
        height: 32px;
      }
    }

    .toggle-responsive {
      height: 100%;
      padding: 0 16px;
      font-weight: 600;
      background: $white;
    }
  }
}

.filter-block-responsive {
  width: 100%;
  max-width: unset;
}

.toggle {
  cursor: pointer;
  position: relative;
  width: 100%;
  background: $achromatic-card-bg;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  color: $achromatic-black;
  height: 32px;
  @include lg-block {
    height: 36px;
    &:hover {
      background-color: #ebecfe;
    }
  }
}

.toggle-responsive {
  background-color: initial;
  justify-content: space-between;
  padding: 16px 0;
  font-weight: 600;
  @include lg-block {
    margin: 24px 0;
  }
}

.toggle-switch {
  display: inline-block;
  background: #d3d3d3;
  border-radius: 16px;
  width: 26px;
  height: 16px;
  position: relative;
  top: -1px;
  vertical-align: middle;
  transition: background 0.25s;
}

.toggle-switch:before,
.toggle-switch:after {
  content: '';
}

.toggle-switch:before {
  display: block;
  background: linear-gradient(to bottom, #fff 0%, #eee 100%);
  border-radius: 50%;
  width: 12px;
  height: 12px;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: left 0.25s;
}

.toggle:hover .toggle-switch:before {
  background: linear-gradient(to bottom, #fff 0%, #fff 100%);
}

.toggle-checkbox:checked + .toggle-switch {
  background: #363ef8;
}

.toggle-checkbox:checked + .toggle-switch:before {
  left: 12px;
}

.toggle-checkbox {
  position: absolute;
  visibility: hidden;
}

.toggle-label {
  margin-right: 6px;
  position: relative;
  letter-spacing: 0.14px;
}

.isChecked {
  border: 1px solid #363ef8;
  height: 32px;
  @include lg-block {
    height: 36px;
  }
}
</style>
